/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import mbpLogger from '@module-logger/mbp';
import { useDispatch, useSelector } from 'react-redux';

import { string } from 'prop-types';
import CarouselBuilderSkeleton from '../../../../components/GraphqlComponents/GraphqlSkeletonComponents/CarouselBuilderSkeleton';
import ComponentErrorBoundary from '../../../../components/GraphqlComponents/EnterpriseID/Common/ComponentErrorBoundary/ComponentErrorBoundary';
import PersonalizedCarouselBuilder from '../../../../components/GraphqlComponents/EnterpriseID/Common/PersonalizedCarouselBuilder';
import { getFeatureFlag } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useTrendingProductsQuery from '../../../../helpers/Personalization/useTrendingProductQuery';
import { emitCustomTrackingEvent, emitProductImpression } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const GiftListRecommendationsSFContainer = ({ title_font_size, title_font_style, title_font_family }) => {
    const isSFGiftListRecsEnabled = useSelector(getFeatureFlag('is-sf-giftlist-recs-enabled'));
    const dispatch = useDispatch();

    if (!isSFGiftListRecsEnabled) { return null; }

    const {
        loading, error, data, variables,
    } = useTrendingProductsQuery({
        queryName: 'GiftListRecommendation',
        pageSize: 12,
        dateRange: 3,
    });

    useEffect(() => {
        const products = data?.trendingProducts?.topProducts?.map((item) => item.product) || [];
        if (!products?.length) {
            return;
        }

        dispatch(emitProductImpression({
            productsPayload: {
                products,
                productPageSize: products.length,
                mobileBreakpoint: 600,
            },
            page: { type: 'giftlist' },
        }));
    }, [data?.trendingProducts]);

    if (loading || variables?.skip) {
        return (
            <CarouselBuilderSkeleton
                nDisplayProducts={{ desktop: 5, tablet: 3, mobile: 2.5 }}
                breakPoints={{ mobile: 601, desktop: 960 }}
            />
        );
    }

    if (error) {
        mbpLogger.logError({
            function: 'GiftListRecommendationsSFContainer.js',
            message: 'Error loading data from trending products api',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
        return null;
    }

    const products = data.trendingProducts?.topProducts?.map((item) => item.product) || [];

    const handleClick = (partNumber) => {
        dispatch(emitCustomTrackingEvent({
            eventType: 'custom',
            eventSubType: 'feature-tracking',
            action: 'Click',
            category: 'Trending Product',
            label: partNumber,
        }));
    };

    if (products?.length > 0) {
        const productsToShow = {
            desktop: 5,
            tablet: 3,
            mobile: 2.5,
        };

        return (
            <ComponentErrorBoundary function="GiftListRecommendationsSFContainer" module="PersonalizedContent">
                <PersonalizedCarouselBuilder
                    blockData={
                        {
                            alignTitle: 'center',
                            productsToShow,
                            title: 'Recommended For You',
                            titleFontSize: title_font_size,
                            titleFontStyle: title_font_style,
                            titleFontFamily: title_font_family,
                        }
                    }
                    productClickCallback={handleClick}
                    products={products}
                    nDisplayProducts={8}
                    demandType="XS"
                    carouselUIComponent="giftListRecommendationsCarousel"
                />
            </ComponentErrorBoundary>
        );
    }

    return null;
};

GiftListRecommendationsSFContainer.propTypes = {
    title_font_size: string,
    title_font_style: string,
    title_font_family: string,
};

GiftListRecommendationsSFContainer.defaultProps = {
    title_font_size: '',
    title_font_style: '',
    title_font_family: '',
};

export default GiftListRecommendationsSFContainer;
